import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { User, Mail, Briefcase, Building2, Lock, Save, KeyRound, Loader2, Shield } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const EYE = "text-[10px] uppercase tracking-[0.15em] text-slate-500 font-mono";

export default function Profile() {
  const { user, refreshUser } = useAuth();
  const [form, setForm] = useState({ name: "", email: "", title: "", department: "" });
  const [pw, setPw] = useState({ current_password: "", new_password: "", confirm: "" });
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPw, setSavingPw] = useState(false);

  useEffect(() => {
    if (user) {
      setForm({
        name: user.name || "",
        email: user.email || "",
        title: user.title || "",
        department: user.department || "",
      });
    }
  }, [user]);

  const saveProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      await api.patch("/auth/me", form);
      await refreshUser();
      toast.success("Profile updated");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    } finally { setSavingProfile(false); }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (pw.new_password !== pw.confirm) { toast.error("New passwords don't match"); return; }
    if (pw.new_password.length < 10) { toast.error("New password must be at least 10 characters"); return; }
    setSavingPw(true);
    try {
      await api.post("/auth/change-password", { current_password: pw.current_password, new_password: pw.new_password });
      setPw({ current_password: "", new_password: "", confirm: "" });
      toast.success("Password changed. Use it next time you sign in.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Password change failed");
    } finally { setSavingPw(false); }
  };

  if (!user) return null;

  return (
    <div className="p-6 lg:p-8 space-y-6 max-w-3xl" data-testid="profile-page">
      <div>
        <div className={EYE + " mb-1"}>// MY PROFILE</div>
        <h1 className="font-display text-3xl text-white tracking-tight">Personal settings</h1>
        <p className="text-slate-400 text-sm mt-1">Update your name, email and password. Your role and permissions are managed by your organisation administrator.</p>
      </div>

      {/* Profile form */}
      <form onSubmit={saveProfile} className="nexus-card p-6 space-y-4" data-testid="profile-form">
        <div className="flex items-start gap-4">
          <div className="w-14 h-14 rounded-full flex items-center justify-center font-mono text-sm shrink-0"
            style={{ background: "linear-gradient(135deg, rgba(212,175,55,0.25), rgba(0,240,255,0.15))", color: "#FCE588", border: "1px solid rgba(212,175,55,0.3)" }}>
            {(form.name || "U").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-white font-medium">{form.name || "—"}</div>
            <div className="text-xs text-slate-400 font-mono">{form.email}</div>
            <div className="text-[10px] text-slate-500 mt-1 font-mono uppercase tracking-widest">
              role · {user.role} {user.is_org_admin && "· org admin"} {user.is_platform_admin && "· platform admin"}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Full name" icon={User}>
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              data-testid="profile-name" className="pr-input" required />
          </Field>
          <Field label="Email" icon={Mail} hint="This is where invitations, resets, and notifications are sent">
            <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
              data-testid="profile-email" className="pr-input" required />
          </Field>
          <Field label="Title" icon={Briefcase}>
            <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
              data-testid="profile-title" className="pr-input" placeholder="e.g. Senior Consultant" />
          </Field>
          <Field label="Department" icon={Building2}>
            <input value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })}
              data-testid="profile-department" className="pr-input" placeholder="e.g. Advisory" />
          </Field>
        </div>

        <div className="flex justify-end pt-2">
          <button type="submit" disabled={savingProfile} data-testid="profile-save"
            className="px-5 py-2 flex items-center gap-2 text-sm font-medium disabled:opacity-40"
            style={{ background: "var(--gold-500)", color: "#050B14" }}>
            {savingProfile ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</> : <><Save className="w-4 h-4" /> Save changes</>}
          </button>
        </div>
      </form>

      {/* Password form */}
      <form onSubmit={changePassword} className="nexus-card p-6 space-y-4" data-testid="password-form">
        <div className="flex items-center gap-2">
          <Lock className="w-4 h-4 text-[#D4AF37]" />
          <div>
            <div className={EYE}>// PASSWORD</div>
            <div className="text-white font-display text-lg mt-1">Change password</div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Field label="Current password"><input type="password" value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} data-testid="pw-current" className="pr-input" required /></Field>
          <Field label="New password"><input type="password" value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} data-testid="pw-new" className="pr-input" required minLength={10} /></Field>
          <Field label="Confirm new"><input type="password" value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} data-testid="pw-confirm" className="pr-input" required /></Field>
        </div>

        <div className="flex justify-end">
          <button type="submit" disabled={savingPw} data-testid="pw-save"
            className="px-5 py-2 flex items-center gap-2 text-sm font-medium disabled:opacity-40 border border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/8">
            {savingPw ? <><Loader2 className="w-4 h-4 animate-spin" /> Updating…</> : <><KeyRound className="w-4 h-4" /> Change password</>}
          </button>
        </div>
      </form>

      {/* MFA hint */}
      <div className="nexus-card p-4 flex items-start gap-3" data-testid="mfa-hint">
        <Shield className="w-4 h-4 text-[#D4AF37] mt-0.5" />
        <div className="flex-1 text-xs text-slate-300 leading-relaxed">
          Want stronger sign-in security?{" "}
          <a href="/security" className="text-[#D4AF37] hover:underline">Enable two-factor authentication (2FA)</a>{" "}
          from the Security page.
        </div>
      </div>

      <style>{`.pr-input { width:100%; padding:0.55rem 0.75rem; background:#050B14; border:1px solid rgba(255,255,255,0.1); color:#F8FAFC; font-family:'IBM Plex Mono', monospace; font-size:0.875rem; outline:none; } .pr-input:focus { border-color:#D4AF37; }`}</style>
    </div>
  );
}

const Field = ({ label, icon: Icon, hint, children }) => (
  <div>
    <label className={EYE + " flex items-center gap-1.5 mb-1.5"}>
      {Icon && <Icon className="w-3 h-3" />} {label}
    </label>
    {children}
    {hint && <div className="text-[11px] text-slate-500 mt-1">{hint}</div>}
  </div>
);
